import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
  Chip,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Publish as PublishIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import descuentoService, {
  DescuentoDto,
  ESTADO_DESCUENTO_LABEL,
  EstadoDescuento,
  describirDimensiones,
  descuentoKeyId,
  hoyIso,
  normalizarEstadoDescuento,
  toEditSearch,
} from '../../services/descuentoService';
import { getApiErrorMessage } from '../../utils/apiError';

const formatFecha = (value?: string | null): string => {
  if (!value) return '—';
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('es-AR');
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  return d.toLocaleDateString('es-AR');
};

const formatPorc = (value?: number | null): string => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n} %`;
};

const formatCantidadDesde = (value?: number | null): string => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n.toLocaleString('es-AR') : '—';
};

const ESTADO_COLOR = { P: 'warning', A: 'success', I: 'default' } as const;

/** Motivo por el que no se puede publicar hoy, o '' si se puede. */
const bloqueoPublicar = (d: DescuentoDto): string => {
  const hoy = hoyIso();
  const desde = String(d.fechaDesde ?? '').slice(0, 10);
  const hasta = String(d.fechaVigencia ?? '').slice(0, 10);
  if (desde && desde > hoy) return `Se podrá publicar a partir del ${formatFecha(desde)}`;
  if (hasta && hasta < hoy) return 'Vencido';
  return '';
};

const Descuentos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState<DescuentoDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoDescuento | ''>('');
  const [loading, setLoading] = useState(false);

  const fetchDescuentos = useCallback(
    async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
      try {
        setLoading(true);
        const data = await descuentoService.getDescuentos({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
          estado: estadoFiltro || undefined,
        });
        setItems(data.items);
        setTotalCount(data.totalCount);
      } catch (error: unknown) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar ofertas'), { variant: 'error' });
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar, estadoFiltro, page, rowsPerPage, searchTerm],
  );

  useEffect(() => {
    fetchDescuentos(0, rowsPerPage, searchTerm);
    // Carga inicial
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchDescuentos(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, estadoFiltro]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchDescuentos(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchDescuentos(0, newRowsPerPage, searchTerm);
  };

  const handleEdit = (d: DescuentoDto) => {
    navigate(`/inventario/descuentos/editar?${toEditSearch(d)}`, { state: { descuento: d } });
  };

  const handleCambiarEstado = async (d: DescuentoDto, accion: 'publicar' | 'inactivar') => {
    if (accion === 'inactivar' && !window.confirm('¿Inactivar esta oferta? Deja de aplicarse en los documentos.')) {
      return;
    }
    try {
      if (accion === 'publicar') await descuentoService.publicar(d);
      else await descuentoService.inactivar(d);
      enqueueSnackbar(accion === 'publicar' ? 'Oferta publicada: ya está en vigencia.' : 'Oferta inactivada.', {
        variant: 'success',
      });
      fetchDescuentos(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cambiar el estado de la oferta'), { variant: 'error' });
    }
  };

  const renderAcciones = (d: DescuentoDto) => {
    const estado = normalizarEstadoDescuento(d.estado);
    const bloqueo = estado === 'A' ? '' : bloqueoPublicar(d);
    return (
      <>
        <Tooltip title={estado === 'A' ? 'Ver' : 'Editar'}>
          <IconButton size="small" onClick={() => handleEdit(d)} color="primary">
            {estado === 'A' ? <VisibilityIcon /> : <EditIcon />}
          </IconButton>
        </Tooltip>
        {estado !== 'A' && (
          <Tooltip title={bloqueo || 'Publicar'}>
            <span>
              <IconButton
                size="small"
                color="success"
                disabled={Boolean(bloqueo)}
                onClick={() => handleCambiarEstado(d, 'publicar')}
              >
                <PublishIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {estado === 'A' && (
          <Tooltip title="Inactivar">
            <IconButton size="small" color="warning" onClick={() => handleCambiarEstado(d, 'inactivar')}>
              <BlockIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Eliminar">
          <IconButton size="small" onClick={() => handleDelete(d)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </>
    );
  };

  const renderEstado = (d: DescuentoDto) => {
    const estado = normalizarEstadoDescuento(d.estado);
    return <Chip size="small" label={ESTADO_DESCUENTO_LABEL[estado]} color={ESTADO_COLOR[estado]} />;
  };

  const handleDelete = async (d: DescuentoDto) => {
    if (!window.confirm('¿Está seguro de eliminar esta oferta?')) return;
    try {
      await descuentoService.remove(d);
      enqueueSnackbar('Oferta eliminada exitosamente', { variant: 'success' });
      fetchDescuentos(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al eliminar la oferta'), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Ofertas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Ofertas con vigencia por cliente, ruta, lista, artículo u otra dimensión, con tramos opcionales por cantidad. El backend define a quién aplica.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/inventario/descuentos/nuevo')}
            >
              Nueva oferta
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              select
              label="Estado"
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value as EstadoDescuento | '')}
              sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="P">Pendiente</MenuItem>
              <MenuItem value="A">Activo</MenuItem>
              <MenuItem value="I">Inactivo</MenuItem>
            </TextField>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Stack>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography color="text.secondary">Cargando...</Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {items.map((d) => (
                <Card key={descuentoKeyId(d)} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight={600}>
                        {formatPorc(d.porcDesc)}
                      </Typography>
                      {renderEstado(d)}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {describirDimensiones(d)}
                      {Number(d.cantidadDesde) > 0 ? ` · Desde ${formatCantidadDesde(d.cantidadDesde)} u.` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Desde: {formatFecha(d.fechaDesde)} · Hasta: {formatFecha(d.fechaVigencia)}
                      {d.fechaAprobacion ? ` · Publicado: ${formatFecha(d.fechaAprobacion)}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {renderAcciones(d)}
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Aplica a</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Cant. desde
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      %
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Desde</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Hasta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Publicado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((d) => (
                    <TableRow key={descuentoKeyId(d)} hover>
                      <TableCell>{describirDimensiones(d)}</TableCell>
                      <TableCell align="right">{formatCantidadDesde(d.cantidadDesde)}</TableCell>
                      <TableCell align="right">{formatPorc(d.porcDesc)}</TableCell>
                      <TableCell>{formatFecha(d.fechaDesde)}</TableCell>
                      <TableCell>{formatFecha(d.fechaVigencia)}</TableCell>
                      <TableCell>{renderEstado(d)}</TableCell>
                      <TableCell>{formatFecha(d.fechaAprobacion)}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {renderAcciones(d)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                          No hay ofertas para mostrar.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Descuentos;